// User-token repo bindings data layer (arc 101 slice 05). A member who connected
// their own fine-grained PAT (`./github-token.ts`) picks which of its allowlisted
// repos developerz.ai drives AS them — no GitHub App on those repos. Reads/writes
// `/v1/me/repo-bindings` (`write:self:keys`, a user session). The bind runs a live
// GitHub probe server-side; a refusal's problem `detail` is the sentence to show.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

export const REPO_BINDINGS_QUERY_KEY = 'repo_bindings';

/** One of the caller's `user_token` bindings on the active account. */
export type RepoBinding = {
  readonly repo_id: string;
  readonly full_name: string;
  readonly enabled: boolean;
};

/** `GET /v1/me/repo-bindings`. */
export type RepoBindingList = { bindings: RepoBinding[] };
/** `POST /v1/me/repo-bindings` (201). */
export type RepoBindResult = {
  bound: true;
  repo_id: string;
  full_name: string;
  created: boolean;
};
/** `DELETE /v1/me/repo-bindings/:owner/:repo` (200). */
export type RepoUnbindResult = { bound: false; full_name: string };

/** The caller's own bindings. Idle with no session, like the other `/v1/me/*` reads. */
export function useRepoBindings(token: string | null = SESSION_TOKEN) {
  return createQuery<RepoBindingList>(() => ({
    queryKey: [REPO_BINDINGS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<RepoBindingList>('/v1/me/repo-bindings', token),
  }));
}

/** Bind `owner/name` to the caller's PAT. A refusal throws `ApiError` whose `detail` names why. */
export async function bindRepo(
  repo: string,
  token: string | null = SESSION_TOKEN,
): Promise<RepoBindResult> {
  return apiFetch('/v1/me/repo-bindings', token, {
    method: 'POST',
    body: JSON.stringify({ repo }),
  });
}

/** Drop the caller's binding on `owner/name` (404 when there was none). */
export async function unbindRepo(
  repo: string,
  token: string | null = SESSION_TOKEN,
): Promise<RepoUnbindResult> {
  const [owner = '', name = ''] = repo.split('/');
  return apiFetch(
    `/v1/me/repo-bindings/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
    token,
    { method: 'DELETE' },
  );
}

/**
 * Is `repo` (an allowlist entry, the casing the user typed) among the bindings
 * (GitHub's canonical casing)? GitHub names are case-insensitive, so the match
 * folds case — the same rule the server's allowlist check applies.
 */
export function isBound(bindings: readonly RepoBinding[], repo: string): boolean {
  const want = repo.toLowerCase();
  return bindings.some((b) => b.full_name.toLowerCase() === want);
}
